import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { Card } from '@/components/ui';
import { useAdminPermission } from '@/lib/useAdminPermission';

/**
 * BlockBanControl — the trust-&-safety write shared by CustomerDrawer and
 * DriverDrawer. Renders a single gated action that, depending on the account's
 * current status, either BLOCKS/BANS the user (active → blocked) or REINSTATES
 * them (blocked → active). The write posts to admin-svc, which proxies auth-svc
 * set-status (status flip + token revocation), so the user is logged out
 * everywhere immediately; a banned driver also drops out of dispatch.
 *
 * RBAC: only ops + superadmin hold customers.block / drivers.ban, so others
 * don't see the control. (admin-svc enforces the same matrix and 403s
 * regardless — this is a UX affordance, not the security boundary.)
 *
 * On success it invalidates the parent detail query (['admin','customer'|'driver',id])
 * so the status badge flips without a manual refresh.
 */
export function BlockBanControl({
  scope,
  id,
  status,
}: {
  scope: 'customer' | 'driver';
  id: number;
  /** The current auth_users.status ('active' | 'blocked' | …). */
  status: string;
}) {
  // customers.block gates the customer scope; drivers.ban gates the driver scope.
  const perm = scope === 'customer' ? 'customers.block' : 'drivers.ban';
  const canAct = useAdminPermission(perm);
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const isBlocked = status === 'blocked';
  // The two verbs differ per scope (customer is "blocked", driver is "banned").
  const verb = scope === 'customer' ? 'block' : 'ban';
  const action: 'block' | 'unblock' | 'ban' | 'unban' = isBlocked
    ? scope === 'customer'
      ? 'unblock'
      : 'unban'
    : verb;

  const mutation = useMutation({
    mutationFn: () =>
      scope === 'customer'
        ? adminApi.blockCustomer(id, action as 'block' | 'unblock', reason)
        : adminApi.banDriver(id, action as 'ban' | 'unban', reason),
    onSuccess: () => {
      setConfirming(false);
      setReason('');
      qc.invalidateQueries({ queryKey: ['admin', scope, id] });
    },
  });

  // Hide entirely for roles without the capability (server enforces too).
  if (!canAct) return null;

  const title = isBlocked
    ? `Reinstate ${scope}`
    : scope === 'customer'
      ? 'Block customer'
      : 'Ban driver';
  const blurb = isBlocked
    ? `Restores access (status → active). The ${scope} can log in again${
        scope === 'driver' ? ' and is eligible for dispatch once more' : ''
      }. Audited.`
    : scope === 'customer'
      ? 'Blocks the account, kills every active session, and prevents future logins (status → blocked, token revoked). Audited.'
      : 'Bans the driver, kills every active session, removes them from dispatch eligibility, and prevents future logins (status → blocked, token revoked). Audited.';

  // The confirm button is danger-toned when blocking/banning, ok-toned when
  // reinstating, so the destructive action reads clearly.
  const confirmCls = isBlocked
    ? 'rounded-md bg-ok px-3 py-1.5 text-xs font-semibold text-white hover:bg-ok/90 disabled:opacity-50'
    : 'rounded-md bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:bg-danger/90 disabled:opacity-50';
  const toggleCls = isBlocked
    ? 'shrink-0 rounded-md bg-ok/15 px-2.5 py-1 text-xs font-medium text-ok ring-1 ring-inset ring-ok/30 hover:bg-ok/25 disabled:opacity-40'
    : 'shrink-0 rounded-md bg-danger/15 px-2.5 py-1 text-xs font-medium text-danger ring-1 ring-inset ring-danger/30 hover:bg-danger/25 disabled:opacity-40';

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-100">{title}</div>
          <p className="mt-1 text-xs text-muted">{blurb}</p>
        </div>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => {
            setConfirming((v) => !v);
            mutation.reset();
          }}
          className={toggleCls}
        >
          {isBlocked ? 'Reinstate' : title.split(' ')[0]}
        </button>
      </div>

      {confirming ? (
        <div className="mt-2 flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <label className="block text-[11px] text-text-secondary">Reason (recorded in the audit trail)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                isBlocked ? 'e.g. appeal upheld / mistaken block' : 'e.g. fraud / safety report / chargeback abuse'
              }
              className="input mt-1"
            />
          </div>
          <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate()} className={confirmCls}>
            {mutation.isPending
              ? 'Working…'
              : isBlocked
                ? 'Confirm reinstate'
                : scope === 'customer'
                  ? 'Confirm block'
                  : 'Confirm ban'}
          </button>
        </div>
      ) : null}

      {mutation.isSuccess ? <p className="mt-2 text-xs text-ok">{mutation.data?.message}</p> : null}
      {mutation.isError ? <p className="mt-2 text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
    </Card>
  );
}
